import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, IdCard, ShieldCheck, Smartphone, ClipboardList, UserCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { PageMasthead } from "@/components/ui-custom/PageMasthead";
import { RevealOnScroll, RevealStagger, staggerItem } from "@/components/ui-custom/RevealOnScroll";
import { SectionHeading } from "@/components/ui-custom/SectionHeading";
import { motion } from "framer-motion";

const BENEFITS = [
  { icon: UserCheck2, text: "Identiti Ahli Disahkan" },
  { icon: ClipboardList, text: "Profil Profesional" },
  { icon: IdCard, text: "Kad Ahli Digital" },
  { icon: ShieldCheck, text: "Akses Program & Latihan" },
  { icon: CheckCircle2, text: "Direktori Profesional" },
  { icon: Smartphone, text: "PWA Member Portal" },
];

const ELIGIBILITY = [
  "Warganegara Malaysia berumur 18 tahun ke atas",
  "Sihat tubuh badan dan berkelakuan baik",
  "Menetap atau berkhidmat di Sarawak",
  "Bersetuju mematuhi perlembagaan & kod etika PPPS",
];

const STEPS = [
  { n: "01", title: "Mohon Dalam Talian", desc: "Isi borang permohonan keahlian & muat naik dokumen sokongan." },
  { n: "02", title: "Semakan Urusetia", desc: "Permohonan disemak dan disahkan oleh urusetia PPPS." },
  { n: "03", title: "Kelulusan & No. Ahli", desc: "No. Ahli rasmi dijana sebaik permohonan diluluskan." },
  { n: "04", title: "Akses Portal Ahli", desc: "Dapatkan Kad Ahli Digital & akses penuh portal ahli PWA." },
];

export default function Keahlian() {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/public/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="keahlian-page">
      <PageMasthead breadcrumb="KEAHLIAN" title="Sertai Komuniti Profesional Pengawal Peribadi Sarawak" subtitle="Jadilah ahli PPPS yang diiktiraf dan disahkan." />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 space-y-20">
        <RevealOnScroll>
          <SectionHeading number="01" title="Mengapa Sertai" />
          <p className="text-piks-muted leading-relaxed max-w-2xl mb-2">
            Menjadi ahli PPPS bermakna anda diiktiraf sebagai pengawal peribadi profesional yang disahkan, berdisiplin
            dan sebahagian daripada komuniti perlindungan yang dipercayai di Sarawak.
          </p>
        </RevealOnScroll>

        <RevealOnScroll>
          <SectionHeading number="02" title="Syarat Kelayakan" />
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
            {ELIGIBILITY.map((e) => (
              <div key={e} className="flex items-start gap-3 p-4 rounded-xl bg-piks-soft-neutral border border-piks-border">
                <CheckCircle2 className="w-4 h-4 text-piks-red mt-0.5 shrink-0" />
                <p className="text-sm text-piks-muted leading-relaxed">{e}</p>
              </div>
            ))}
          </div>
        </RevealOnScroll>

        <RevealOnScroll>
          <SectionHeading number="03" title="Kategori Keahlian" />
          <RevealStagger className="grid sm:grid-cols-2 gap-6" staggerDelay={0.08}>
            {categories.map((c) => (
              <motion.div key={c.id} variants={staggerItem} className="p-6 rounded-xl bg-white border border-piks-border hover:border-piks-gold/50 hover:-translate-y-1 transition-all duration-300" data-testid={`category-card-${c.code}`}>
                <p className="text-xs font-semibold text-piks-red tracking-wide mb-2">{c.code}</p>
                <h3 className="font-semibold text-piks-black text-lg mb-2">{c.name}</h3>
                <p className="text-sm text-piks-muted leading-relaxed">{c.description}</p>
              </motion.div>
            ))}
          </RevealStagger>
        </RevealOnScroll>

        <RevealOnScroll>
          <SectionHeading number="04" title="Manfaat Keahlian" />
          <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 gap-4" staggerDelay={0.06}>
            {BENEFITS.map((b) => (
              <motion.div key={b.text} variants={staggerItem} className="flex flex-col items-start gap-3 p-5 rounded-xl bg-piks-black text-white">
                <b.icon className="w-5 h-5 text-piks-gold" />
                <span className="text-sm font-medium">{b.text}</span>
              </motion.div>
            ))}
          </RevealStagger>
        </RevealOnScroll>

        <RevealOnScroll>
          <SectionHeading number="05" title="Proses Permohonan" />
          <div className="grid sm:grid-cols-4 gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="relative pl-4 border-l-2 border-piks-gold/40">
                <p className="font-mono text-piks-red font-bold text-sm mb-2">{s.n}</p>
                <h3 className="font-semibold text-piks-black text-sm mb-1.5">{s.title}</h3>
                <p className="text-xs text-piks-muted leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </RevealOnScroll>

        <RevealOnScroll className="bg-piks-black text-white rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="diagonal-accent absolute left-6 top-6 opacity-40">
            <span /><span /><span />
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold mb-3">Ahli PPPS Sedia Ada?</h2>
          <p className="text-white/60 max-w-md mx-auto mb-8">
            Jika anda pernah berdaftar dengan PPPS sebelum ini, tuntut keahlian anda untuk mendapatkan akaun log masuk dan No. Ahli baharu.
          </p>
          <Link to="/tuntut-keahlian">
            <Button size="lg" className="rounded-full h-12 px-8 bg-piks-red hover:bg-piks-red/90 text-white font-bold text-sm" data-testid="keahlian-mohon-btn">
              TUNTUT AHLI
            </Button>
          </Link>
        </RevealOnScroll>
      </section>
    </div>
  );
}
